/**
 * Bidirectional postMessage bridge between this UI (running as an
 * iframe) and its host application. All events are namespaced under a
 * single wire envelope so unrelated postMessage traffic on the parent
 * window is ignored cleanly.
 *
 * Wire format:
 *   { source: 'copilot-ui', type: <BridgeEventType>, payload?: unknown }
 *
 * The host is expected to mirror this envelope with `source: 'copilot-host'`
 * on inbound messages.
 */

import { isInIframe } from './iframe';
import { isPreProdEnvironment } from './environment';
import { ShoulderTapPayload } from '../services/api';
import {
  ServiceHealthContent,
  ShoulderTapFiredContent,
  ShoulderTapStaticContent,
} from '../content/shoulderTapContent';

export const BRIDGE_SOURCE_UI = 'copilot-ui';
export const BRIDGE_SOURCE_HOST = 'copilot-host';

export const BridgeEventType = {
  // UI → host
  ShoulderTapFired: 'shoulder-tap-fired',
  ServiceHealthChanged: 'service-health-changed',
  /**
   * Publishes the static copy bundle. Emitted on mount and in response to
   * `ContentRequest`, so hosts can render chrome (collapsed pill, offline
   * notice) before the first tap arrives.
   */
  CopilotContent: 'copilot-content',
  // host → UI
  ShoulderTapAccept: 'shoulder-tap-accept',
  ShoulderTapDismiss: 'shoulder-tap-dismiss',
  /**
   * Host asks us to (re-)publish the static copy bundle — for hosts that
   * attach their listener after we've mounted, or that remount their
   * overlay.
   */
  ContentRequest: 'content-request',
} as const;
export type BridgeEventType = typeof BridgeEventType[keyof typeof BridgeEventType];

export type ServiceHealthStatus = 'offline' | 'healthy';

/**
 * All user-facing copy for the shoulder-tap flow is owned by this UI and
 * travels on the wire (see `content/shoulderTapContent.ts`). Hosts render
 * from `content` and hold no copy of their own.
 *
 * `content` is additive to the pre-existing payload fields, so hosts that
 * haven't migrated yet keep working unchanged.
 */
export interface ShoulderTapFiredPayload {
  contactId: string;
  /** The tap that just fired. */
  tap: ShoulderTapPayload;
  /**
   * Copy resolved over the whole tap sequence, not just `tap`. Render these
   * values from the latest event — a later tap can change the script.
   */
  content: ShoulderTapFiredContent;
}

export interface ServiceHealthChangedPayload {
  status: ServiceHealthStatus;
  content: ServiceHealthContent;
}

export interface CopilotContentPayload {
  content: ShoulderTapStaticContent;
}

interface BridgeEnvelope<T = unknown> {
  source: typeof BRIDGE_SOURCE_UI | typeof BRIDGE_SOURCE_HOST;
  type: BridgeEventType;
  payload?: T;
}

/**
 * Origins allowed to send us inbound events. Empty array = accept any
 * origin (dev / stub mode). Populate with concrete host origins as they
 * come online.
 */
export const ALLOWED_HOST_ORIGINS: readonly string[] = [];

const isBridgeEnvelope = (data: unknown): data is BridgeEnvelope => {
  if (!data || typeof data !== 'object') return false;
  const envelope = data as Partial<BridgeEnvelope>;
  return envelope.source === BRIDGE_SOURCE_HOST && typeof envelope.type === 'string';
};

const isOriginAllowed = (origin: string): boolean => {
  if (ALLOWED_HOST_ORIGINS.length === 0) {
    // Open-origin fallback is only for dev/stub. In production, an
    // empty allowlist is a misconfiguration — reject inbound events
    // rather than accepting arbitrary parents that could trigger
    // accept/dismiss API calls with the current contactId.
    if (!isPreProdEnvironment()) {
      console.error('[hostBridge] No allowed origins configured in production — rejecting');
      return false;
    }
    return true;
  }
  return ALLOWED_HOST_ORIGINS.includes(origin);
};

/**
 * Emit an event to the host window. No-op when we're not inside an
 * iframe (e.g. running the app standalone in dev).
 */
export const postToHost = <T = unknown>(type: BridgeEventType, payload?: T): void => {
  if (!isInIframe()) return;
  const envelope: BridgeEnvelope<T> = { source: BRIDGE_SOURCE_UI, type, payload };
  // TODO: replace '*' with a captured host origin (handshake — the
  // host's first inbound message provides a browser-attested
  // event.origin we can pin outbound messages to).
  window.parent.postMessage(envelope, '*');
};

/**
 * Subscribe to a specific bridge event coming from the host. Returns
 * an unsubscribe function.
 */
export const subscribeFromHost = <T = unknown>(
  type: BridgeEventType,
  handler: (payload: T | undefined, event: MessageEvent) => void,
): (() => void) => {
  const listener = (event: MessageEvent) => {
    if (!isOriginAllowed(event.origin)) return;
    if (!isBridgeEnvelope(event.data)) return;
    if (event.data.type !== type) return;
    handler(event.data.payload as T | undefined, event);
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};
